import React, { useMemo, useEffect, useState } from 'react'
import { useAppSelector } from '@/redux/hooks'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { useGamePhase } from '@/hooks/useGamePhase'
import { getTeamCommunicatorForRound } from '@/api/team'
import { TeamCommunicator } from '@/redux/types/team'
import { Status } from '@/redux/types/room'
import { RoleChoice } from '@/redux/types/room'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SERVER_URL } from '@/api/config'

type TeamMembersPopoverProps = {
  isTeamRoomActive: boolean
  className?: string
}

const TeamMembersPopover: React.FC<TeamMembersPopoverProps> = ({
  isTeamRoomActive,
  className,
}) => {
  const { user } = useEffectiveUser()
  const { roundId } = useGamePhase()
  const roomMembers = useAppSelector((state) => state.room.roomMembers)
  const currentRoom = useAppSelector((state) => state.room.currentRoom)
  const [teamCommunicator, setTeamCommunicator] =
    useState<TeamCommunicator | null>(null)

  const userTeamId = useMemo(() => {
    if (!user?.email) return null
    const currentMember = roomMembers.find((m) => m.member_email === user.email)
    return currentMember?.teamId ?? null
  }, [user?.email, roomMembers])

  const isTeamMode = useMemo(
    () => currentRoom?.game_mode === TimerMode.TEAM_MODE,
    [currentRoom?.game_mode]
  )

  useEffect(() => {
    const fetchTeamCommunicator = async () => {
      if (!isTeamRoomActive || !isTeamMode || !userTeamId || !roundId) {
        setTeamCommunicator(null)
        return
      }
      try {
        const communicator = await getTeamCommunicatorForRound(
          userTeamId,
          roundId
        )
        setTeamCommunicator(communicator)
      } catch {
        setTeamCommunicator(null)
      }
    }
    fetchTeamCommunicator()
  }, [isTeamRoomActive, isTeamMode, userTeamId, roundId])

  const teamMembers = useMemo(() => {
    if (!user?.email || !isTeamRoomActive) return []
    const currentMember = roomMembers.find((m) => m.member_email === user.email)
    const teamId = currentMember?.teamId
    if (teamId == null) return []
    return roomMembers.filter(
      (m) =>
        m.teamId === teamId &&
        m.role === RoleChoice.PLAYER &&
        m.invitation_status === Status.Accepted
    )
  }, [user?.email, isTeamRoomActive, roomMembers])

  if (!isTeamRoomActive) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className={cn(
            'h-8 w-8 rounded-md text-gray-600 hover:bg-sky-200 hover:text-gray-800',
            className
          )}
          aria-label='View team members'
        >
          <Users className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' side='bottom' className='w-64 p-0'>
        <div className='p-2 border-b bg-sky-50/50'>
          <h3 className='text-sm font-semibold text-gray-800'>
            Team Members ({teamMembers.length})
          </h3>
        </div>
        <div className='max-h-64 overflow-y-auto p-1'>
          {teamMembers.length === 0 ? (
            <p className='p-3 text-sm text-gray-500'>No team members found.</p>
          ) : (
            <ul className='space-y-0.5'>
              {teamMembers.map((member) => {
                const isCurrentUser = member.member_email === user?.email
                const isCommunicator =
                  member.member_email === teamCommunicator?.communicator_email
                const avatarUrl = member.member_profile_picture
                  ? `${SERVER_URL}${member.member_profile_picture}`
                  : undefined
                const initials =
                  member.member_name
                    ?.split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || '?'

                return (
                  <li
                    key={member.member_id ?? member.member_email}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-2 py-2 text-sm',
                      isCurrentUser && 'bg-sky-100'
                    )}
                  >
                    <Avatar className='h-8 w-8 flex-shrink-0'>
                      <AvatarImage
                        src={avatarUrl}
                        alt={member.member_name}
                        className='object-cover'
                      />
                      <AvatarFallback className='bg-sky-200 text-sky-800 text-xs'>
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-1.5'>
                        <p className='truncate font-medium text-gray-900'>
                          {member.member_name || member.member_email}
                        </p>
                        {isCurrentUser && (
                          <span className='text-xs text-gray-500'>(you)</span>
                        )}
                        {isCommunicator && (
                          <Badge
                            variant='secondary'
                            className='text-[10px] px-1.5 py-0 font-medium bg-amber-100 text-amber-800 border-amber-200'
                          >
                            Communicator
                          </Badge>
                        )}
                      </div>
                      <p className='truncate text-xs text-gray-500'>
                        {member.member_email}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TeamMembersPopover
